/**
 * Meta Pixel Helper Functions
 * Safely track events with Facebook Pixel
 * Only active in production environment (NODE_ENV=production), and only
 * once the user has given cookie consent (see cookieConsent.js).
 */

import {hasAcceptedConsent} from './cookieConsent';

const isProduction = process.env.NODE_ENV === 'production';
const PIXEL_ID = '1331957008392341';

let pixelLoaded = false;

/**
 * Injects the Meta Pixel base code and initializes it.
 * Safe to call multiple times - only loads once.
 * Should only be called once the user has accepted cookie consent.
 */
export const loadPixel = () => {
  if (pixelLoaded || !isProduction || typeof window === 'undefined') {
    return;
  }

  /* eslint-disable */
  !function(f,b,e,v,n,t,s)
  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
  n.queue=[];t=b.createElement(e);t.async=!0;
  t.src=v;s=b.getElementsByTagName(e)[0];
  s.parentNode.insertBefore(t,s)}(window, document,'script',
  'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */

  window.fbq('init', PIXEL_ID);
  pixelLoaded = true;
};

/**
 * Track a standard Facebook Pixel event
 * @param {string} eventName - The name of the event (e.g., 'Lead', 'Purchase', 'InitiateCheckout')
 * @param {Object} parameters - Optional parameters for the event
 */
export const trackEvent = (eventName, parameters = {}) => {
  if (!isProduction) {
    console.log(`[FB Pixel] [DEV MODE - NOT SENT] Event: ${eventName}`, parameters);
    return;
  }

  if (!pixelLoaded || !hasAcceptedConsent()) {
    return;
  }

  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', eventName, parameters);
    console.log(`[FB Pixel] Event tracked: ${eventName}`, parameters);
  } else {
    console.warn('[FB Pixel] fbq is not available');
  }
};

/**
 * Track a custom Facebook Pixel event
 * @param {string} eventName - The name of the custom event
 * @param {Object} parameters - Optional parameters for the event
 */
export const trackCustomEvent = (eventName, parameters = {}) => {
  if (!isProduction) {
    console.log(`[FB Pixel] [DEV MODE - NOT SENT] Custom event: ${eventName}`, parameters);
    return;
  }

  if (!pixelLoaded || !hasAcceptedConsent()) {
    return;
  }

  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('trackCustom', eventName, parameters);
    console.log(`[FB Pixel] Custom event tracked: ${eventName}`, parameters);
  } else {
    console.warn('[FB Pixel] fbq is not available');
  }
};

/**
 * Track a Lead event (e.g., after signup)
 */
export const trackLead = () => {
  trackEvent('Lead');
};

/**
 * Track InitiateCheckout event
 */
export const trackInitiateCheckout = () => {
  trackEvent('InitiateCheckout');
};

/**
 * Track Purchase event
 * @param {number} value - The purchase value
 * @param {string} currency - The currency code (default: 'EUR')
 */
export const trackPurchase = (value, currency = 'EUR') => {
  trackEvent('Purchase', {
    value: value,
    currency: currency
  });
};

/**
 * Track a Contact event
 */
export const trackContact = () => {
  trackEvent('Contact');
};

/**
 * Track PageView event
 * Called automatically on route changes in production mode
 */
export const trackPageView = () => {
  trackEvent('PageView');
};
